// Real reachability — supplements navigator.onLine with an actual network probe.
// Captive portals and local network outages report onLine=true while the server
// is unreachable; this catches those cases.

const POLL_INTERVAL_MS = 30_000;
const DEBOUNCE_MS = 500;
// A single failed probe is not evidence of an outage — one dropped packet on a
// rural link would otherwise blank out every write action. Two in a row is.
const FAILURES_BEFORE_OFFLINE = 2;

type Listener = () => void;

let online = navigator.onLine;
let checkUrl = "";
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let consecutiveFailures = 0;
// Last-started-wins ordering. Probes are fired by four independent triggers
// (boot, the 30s poll, `visibilitychange`, `probeNow`) and resolve in
// completion order, not start order — a slow failure started first can land
// after a fast success started later and write a stale verdict over a fresh
// one. Each probe captures its generation and drops its own result if a newer
// probe has started since.
let generation = 0;
const listeners = new Set<Listener>();

function notify() {
	for (const fn of listeners) fn();
}

function scheduleNotify(nextState: boolean) {
	if (debounceTimer !== null) clearTimeout(debounceTimer);
	debounceTimer = setTimeout(() => {
		debounceTimer = null;
		if (nextState !== online) {
			online = nextState;
			notify();
		}
	}, DEBOUNCE_MS);
}

function recordSuccess() {
	// A single success is enough to recover, and it cancels any pending offline
	// transition (scheduleNotify replaces the debounce timer).
	consecutiveFailures = 0;
	scheduleNotify(true);
}

function recordFailure() {
	consecutiveFailures += 1;
	if (consecutiveFailures < FAILURES_BEFORE_OFFLINE) return;
	scheduleNotify(false);
}

/** Resolves with this probe's verdict: true when the server answered. */
async function probe(): Promise<boolean> {
	if (!checkUrl) return online;
	const gen = ++generation;
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), 5_000);
	try {
		// Any HTTP response — including 401/403 from the authenticated API —
		// proves the server is reachable. Only a network error or timeout means
		// we are truly offline (captive portal, cable unplugged, etc.).
		await fetch(checkUrl, {
			method: "HEAD",
			cache: "no-store",
			mode: "no-cors",
			signal: controller.signal,
		});
		clearTimeout(timeoutId);
		if (gen !== generation) return online;
		recordSuccess();
		return true;
	} catch {
		clearTimeout(timeoutId);
		if (gen !== generation) return online;
		recordFailure();
		return false;
	}
}

/**
 * Force an immediate probe — used when the user hits an action we are blocking,
 * so a stale offline verdict is re-tested at the moment it actually costs them
 * something. Reuses the existing probe path: no new listener, no new timer.
 */
export function probeNow(): Promise<boolean> {
	return probe();
}

function onBrowserOnline() {
	// Browser says online — verify with a real probe before trusting it.
	probe();
}

function onBrowserOffline() {
	// Browser says offline — trust immediately (faster UX).
	scheduleNotify(false);
}

function onVisibilityChange() {
	if (document.visibilityState === "visible") probe();
}

let initialized = false;
let pollHandle: ReturnType<typeof setInterval> | null = null;

export function initReachability(url: string) {
	checkUrl = url;
	if (initialized) return;
	initialized = true;

	window.addEventListener("online", onBrowserOnline);
	window.addEventListener("offline", onBrowserOffline);
	document.addEventListener("visibilitychange", onVisibilityChange);

	// Kick an immediate probe to establish real state on boot.
	probe();

	pollHandle = setInterval(probe, POLL_INTERVAL_MS);
}

export function getSnapshot(): boolean {
	return online;
}

export function subscribe(fn: Listener): () => void {
	listeners.add(fn);
	return () => listeners.delete(fn);
}

// Exposed for unit tests only — allows resetting module state between test cases.
export function _resetForTesting(initialOnline = true) {
	online = initialOnline;
	checkUrl = "";
	initialized = false;
	consecutiveFailures = 0;
	generation++;
	if (pollHandle !== null) {
		clearInterval(pollHandle);
		pollHandle = null;
	}
	if (debounceTimer !== null) {
		clearTimeout(debounceTimer);
		debounceTimer = null;
	}
	window.removeEventListener("online", onBrowserOnline);
	window.removeEventListener("offline", onBrowserOffline);
	document.removeEventListener("visibilitychange", onVisibilityChange);
	listeners.clear();
}
